import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  Check,
  Star,
  ShoppingBag,
  Globe,
  ChevronDown,
  Sun,
  Moon,
  LogIn,
  Truck,
  Headphones
} from "lucide-react";
import LogoIndak from "../../assets/img/logo-pdf.png";
import ImageMoneyTransferAI from "../../assets/img/money_transfer_ai.png";
import { getAutoTheme } from "../../utils/themeHelper";
import "../home/home.css";

export default function Services() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(localStorage.getItem("lang") || "en");
  const [langOpen, setLangOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState(getAutoTheme);
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

  useEffect(() => {
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;

    // Scroll Reveal Intersection Observer
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("revealed");
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll(".reveal, .reveal-left, .reveal-right, .reveal-scale").forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [lang]);

  // Sync theme
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("landing-theme", newTheme);
  };

  const changeLanguage = (newLang) => {
    setLang(newLang);
    localStorage.setItem("lang", newLang);
    setLangOpen(false);
  };

  const text = {
    en: {
      navHome: "Home",
      navAbout: "About",
      navServices: "Services",
      navContact: "Contact",
      navCheckRates: "Check Rates",
      navSignIn: "Sign In",
      navDashboard: "Dashboard",
      badgeServices: "Logistics Solutions",
      heroTitle: "Our Professional Services",
      heroSubtitle: "Integrated cargo delivery and instant money transfer solutions from Indonesia to Yemen, Saudi Arabia, and across the Middle East with a commitment to speed and safety.",
      featuresBadge: "Our Advantages",
      featuresTitle: "Key Service Features",
      featuresDesc: "Why thousands of exporters and individual shippers trust their logistics with INDAK.",
      feat1Title: "Wide Coverage",
      feat1Desc: "Our local courier network in Yemen and Saudi Arabia reaches various cities and remote areas safely.",
      feat2Title: "Fast Shipping",
      feat2Desc: "Optimizing weekly flight cargo schedules to minimize wait times at airport transit warehouses.",
      feat3Title: "Secure Handling",
      feat3Desc: "Every package is wrapped in extra layers and placed in protective boxes if necessary to guarantee safety.",
      integrationBadge: "E-Commerce Ready",
      integrationTitle: "Integrated with Top Indonesian E-Commerce",
      integrationDesc: "We support shipping goods you purchased from leading Indonesian e-commerce platforms directly to the Middle East. Simply order, ship to our Jakarta Hub, and we'll handle the rest.",
      workBadge: "How We Work",
      workTitle: "Professional Logistics",
      workDesc: "Our standard operating procedures ensure transparency and integrity at every stage of transit.",
      work1Title: "Timely Shipments",
      work1Desc: "Weekly scheduled departures ensure your goods don't sit long and arrive on time.",
      work2Title: "Secure Packaging",
      work2Desc: "Physical checks of package quality before container loading to minimize damage risks.",
      work3Title: "Wide Reach",
      work3Desc: "Strategic partnerships with local agents in Yemen ensure smooth final mile delivery.",
      testimonialQuote: "INDAK has been extremely reliable for my business. Sending goods to my family in Yemen has never been easier and faster. Very recommended!",
      testimonialAuthor: "- Amin",
      footerDesc: "Trusted international logistics and remittance solutions from Indonesia to Yemen and Saudi Arabia.",
      footerCopy: "All rights reserved."
    },
    ar: {
      navHome: "الرئيسية",
      navAbout: "من نحن",
      navServices: "خدماتنا",
      navContact: "اتصل بنا",
      navCheckRates: "أسعار الشحن",
      navSignIn: "تسجيل الدخول",
      navDashboard: "لوحة التحكم",
      badgeServices: "حلول لوجستية",
      heroTitle: "خدماتنا الاحترافية",
      heroSubtitle: "حلول شحن البضائع المتكاملة والتحويلات المالية الفورية من إندونيسيا إلى اليمن، المملكة العربية السعودية، وأنحاء الشرق الأوسط بملتزمي السرعة والأمان.",
      featuresBadge: "مميزاتنا",
      featuresTitle: "أبرز مميزات الخدمة",
      featuresDesc: "لماذا يثق آلاف المصدرين والمرسلين الأفراد بلوجستياتهم مع إنداك.",
      feat1Title: "تغطية واسعة",
      feat1Desc: "تصل شبكة الموزعين المحليين لدينا في اليمن والمملكة العربية السعودية إلى مختلف المدن والمناطق النائية بأمان.",
      feat2Title: "شحن سريع",
      feat2Desc: "تحسين جداول رحلات الشحن الجوي الأسبوعية لتقليل أوقات الانتظار في مستودعات عبور المطارات.",
      feat3Title: "معاملة آمنة",
      feat3Desc: "يتم تغليف كل طرد بطبقات إضافية ووضعه في صناديق واقية إذا لزم الأمر لضمان السلامة.",
      integrationBadge: "تكامل المتجر الإلكتروني",
      integrationTitle: "متكامل مع أشهر منصات التجارة الإلكترونية الإندونيسية",
      integrationDesc: "نحن ندعم شحن البضائع التي تشتريها من منصات التجارة الإلكترونية الإندونيسية الرائدة مباشرة إلى الشرق الأوسط. ما عليك سوى الطلب، والشحن إلى مستودعنا في جاكرتا، وسنتولى نحن الباقي.",
      workBadge: "طريقة عملنا",
      workTitle: "الخدمات اللوجستية المهنية",
      workDesc: "تضمن إجراءات التشغيل القياسية لدينا الشفافية والنزاهة في كل مرحلة من مراحل النقل.",
      work1Title: "شحنات في الوقت المحدد",
      work1Desc: "تضمن المغادرات الأسبوعية المجدولة عدم بقاء بضائعكم طويلاً ووصولها في الوقت المحدد.",
      work2Title: "تعبئة آمنة ومحمية",
      work2Desc: "فحوصات مادية لجودة الطرد قبل تحميل الحاوية لتقليل مخاطر التلف.",
      work3Title: "وصول واسع",
      work3Desc: "تضمن الشراكات الاستراتيجية مع الوكلاء المحليين في اليمن سلاسة التسليم النهائي.",
      testimonialQuote: "لقد كانت شركة إنداك موثوقة للغاية لأعمالي التجارية. إن إرسال البضائع إلى عائلتي في اليمن لم يكن أبداً أسهل أو أسرع من ذلك. نوصي بها بشدة!",
      testimonialAuthor: "- أمين",
      footerDesc: "حلول لوجستية وحوالات مالية دولية موثوقة من إندونيسيا إلى اليمن والمملكة العربية السعودية.",
      footerCopy: "جميع الحقوق محفوظة."
    },
    id: {
      navHome: "Beranda",
      navAbout: "Tentang Kami",
      navServices: "Layanan",
      navContact: "Kontak",
      navCheckRates: "Cek Ongkir",
      navSignIn: "Masuk",
      navDashboard: "Dashboard",
      badgeServices: "Portofolio Layanan",
      heroTitle: "Layanan Ekspedisi Kami",
      heroSubtitle: "Solusi kargo dan pengiriman uang lengkap yang disesuaikan untuk memenuhi kebutuhan personal dan bisnis Anda ke Timur Tengah.",
      catCargo: "Layanan Kargo",
      catRemit: "Transfer Uang",
      catBuy: "Pembalian & Titip Kirim",
      work1Title: "Proses Transparan",
      work1Desc: "Pelacakan resi real-time memberikan visibilitas penuh untuk setiap tahapan pengiriman.",
      work2Title: "Pengemasan Aman & Dilindungi",
      work2Desc: "Pemeriksaan fisik standar kualitas paket sebelum pemuatan peti kemas untuk meminimalkan risiko kerusakan.",
      work3Title: "Jangkauan Luas",
      work3Desc: "Kemitraan strategis dengan agen lokal di Yaman dan Arab Saudi memastikan kelancaran pengiriman akhir.",
      testimonialQuote: "INDAK sangat andal untuk bisnis saya. Mengirim barang ke keluarga saya di Yaman tidak pernah semudah atau secepat ini. Sangat direkomendasikan!",
      testimonialAuthor: "- Amin",
      footerDesc: "Solusi logistik dan transfer uang internasional terpercaya dari Indonesia ke Yaman dan Arab Saudi.",
      footerCopy: "Hak cipta dilindungi undang-undang."
    }
  };

  const t = text[lang] || text.en;

  return (
    <div className="landing-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* HEADER NAVBAR */}
      <header className="landing-header">
        <div className="header-container">
          <div className="header-logo" onClick={() => navigate("/")}>
            <img src={LogoIndak} alt="Indak Logo" className="logo-img" />
          </div>

          <nav className="desktop-nav">
            <button onClick={() => navigate("/")} className="nav-link">{t.navHome}</button>
            <button onClick={() => navigate("/about")} className="nav-link">{t.navAbout}</button>
            <button onClick={() => navigate("/services")} className="nav-link active-link-highlight">{t.navServices}</button>
            <button onClick={() => navigate("/contact")} className="nav-link">{t.navContact}</button>
            <button onClick={() => navigate("/check-rates")} className="nav-link">{t.navCheckRates}</button>

            {/* Language Switcher */}
            <div className="lang-dropdown-container">
              <button className="lang-dropdown-trigger" onClick={() => setLangOpen(!langOpen)}>
                <Globe size={16} />
                <span>{lang === "en" ? "EN" : lang === "id" ? "ID" : "AR"}</span>
                <ChevronDown size={14} />
              </button>
              {langOpen && (
                <div className="lang-dropdown-menu">
                  <button className={`lang-dropdown-item ${lang === "en" ? "active-lang" : ""}`} onClick={() => changeLanguage("en")}>
                    English
                  </button>
                  <button className={`lang-dropdown-item ${lang === "id" ? "active-lang" : ""}`} onClick={() => changeLanguage("id")}>
                    Bahasa Indonesia
                  </button>
                  <button className={`lang-dropdown-item ${lang === "ar" ? "active-lang" : ""}`} onClick={() => changeLanguage("ar")}>
                    العربية
                  </button>
                </div>
              )}
            </div>

            {/* Theme Toggle Button */}
            <button onClick={toggleTheme} className="theme-toggle-btn" title={theme === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"}>
              {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
            </button>

            {/* Login Door Icon Button */}
            <button
              onClick={() => navigate(isLoggedIn ? "/admin/dashboard" : "/login")}
              className="nav-sig-button"
              title={isLoggedIn ? t.navDashboard : t.navSignIn}
            >
              <LogIn size={20} />
            </button>
          </nav>

          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="mobile-nav">
            <button onClick={() => navigate("/")} className="mobile-nav-link">{t.navHome}</button>
            <button onClick={() => navigate("/about")} className="mobile-nav-link">{t.navAbout}</button>
            <button onClick={() => navigate("/services")} className="mobile-nav-link">{t.navServices}</button>
            <button onClick={() => navigate("/contact")} className="mobile-nav-link">{t.navContact}</button>
            <button onClick={() => navigate("/check-rates")} className="mobile-nav-link">{t.navCheckRates}</button>

            <div style={{ display: "flex", gap: "20px", padding: "12px 0", alignItems: "center" }}>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => changeLanguage("en")}
                  style={{ background: "none", border: "none", color: lang === "en" ? "#c82333" : "#aaa", fontWeight: "700", cursor: "pointer" }}
                >
                  EN
                </button>
                <span style={{ color: "#444" }}>|</span>
                <button
                  onClick={() => changeLanguage("id")}
                  style={{ background: "none", border: "none", color: lang === "id" ? "#c82333" : "#aaa", fontWeight: "700", cursor: "pointer" }}
                >
                  ID
                </button>
                <span style={{ color: "#444" }}>|</span>
                <button
                  onClick={() => changeLanguage("ar")}
                  style={{ background: "none", border: "none", color: lang === "ar" ? "#c82333" : "#aaa", fontWeight: "700", cursor: "pointer" }}
                >
                  AR
                </button>
              </div>

              <button
                onClick={toggleTheme}
                style={{ background: "none", border: "none", color: "var(--header-link)", cursor: "pointer", padding: "4px" }}
              >
                {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
              </button>
            </div>

            <button onClick={() => navigate(isLoggedIn ? "/admin/dashboard" : "/login")} className="mobile-nav-sig-cta">
              <LogIn size={18} /> {isLoggedIn ? t.navDashboard : t.navSignIn}
            </button>
          </div>
        )}
      </header>

      {/* SERVICES HERO */}
      <section className="inner-hero-section">
        <div className="section-container">
          <div className="inner-hero-grid">
            <div className="hero-content reveal-left">
              <span className="section-badge">{t.badgeServices}</span>
              <h1 className="hero-title">
                {lang === "ar" ? (
                  <>
                    خدماتنا <span className="text-gradient">الاحترافية</span>
                  </>
                ) : (
                  <>
                    Our Professional <br />
                    <span className="text-gradient">Services</span>
                  </>
                )}
              </h1>
              <p className="hero-subtitle">
                {t.heroSubtitle}
              </p>
            </div>
            <div className="hero-visual-box reveal-right">
              <img src={ImageMoneyTransferAI} alt="Indak Services cargo" className="inner-hero-img" />
            </div>
          </div>
        </div>
      </section>

      {/* DETAILED SERVICES LIST (With Motion Reveal) */}
      <section className="services-detail-section">
        <div className="section-container">
          <div className="section-header-center reveal">
            <span className="section-badge">{t.featuresBadge}</span>
            <h2 className="section-title">{t.featuresTitle}</h2>
            <p className="section-desc-center">
              {t.featuresDesc}
            </p>
          </div>

          <div className="services-detail-list">
            <div className="service-detail-card reveal-scale reveal-delay-1">
              <div className="detail-card-number">01</div>
              <h4>{t.feat1Title}</h4>
              <p>{t.feat1Desc}</p>
              <ul className="service-bullets">
                <li><Check size={16} /> {lang === "ar" ? "تريم، سيئون، المكلا، عدن، صنعاء" : "Tarim, Sewun, Mukalla, Aden, Sana'a"}</li>
                <li><Check size={16} /> {lang === "ar" ? "جدة، الرياض، مكة، المدينة" : "Jeddah, Riyadh, Makkah, Madinah"}</li>
              </ul>
            </div>

            <div className="service-detail-card reveal-scale reveal-delay-2">
              <div className="detail-card-number">02</div>
              <h4>{t.feat2Title}</h4>
              <p>{t.feat2Desc}</p>
              <ul className="service-bullets">
                <li><Check size={16} /> {lang === "ar" ? "مواعيد مغادرة دقيقة" : "Precise departure schedules"}</li>
                <li><Check size={16} /> {lang === "ar" ? "أولوية التخليص الجمركي" : "Priority customs clearance"}</li>
              </ul>
            </div>

            <div className="service-detail-card reveal-scale reveal-delay-3">
              <div className="detail-card-number">03</div>
              <h4>{t.feat3Title}</h4>
              <p>{t.feat3Desc}</p>
              <ul className="service-bullets">
                <li><Check size={16} /> {lang === "ar" ? "تأمين شامل للشحنات" : "Comprehensive shipment insurance"}</li>
                <li><Check size={16} /> {lang === "ar" ? "تعبئة مضادة للصدمات" : "Anti-shock secure packaging"}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ONLINE SHOP INTEGRATION (With Motion Reveal) */}
      <section className="services-integration-section reveal">
        <div className="section-container">
          <div className="integration-card">
            <span className="section-badge">{t.integrationBadge}</span>
            <h3 className="section-title text-white">{t.integrationTitle}</h3>
            <p className="section-desc">
              {t.integrationDesc}
            </p>
            <div className="shop-logos-grid">
              <a href="https://www.tokopedia.com/" target="_blank" rel="noopener noreferrer" className="shop-logo-link">
                <img src="https://upload.wikimedia.org/wikipedia/commons/f/fe/Shopee.svg" alt="Shopee Logo" className="shop-logo-img" />
              </a>
              <a href="https://shopee.co.id/" target="_blank" rel="noopener noreferrer" className="shop-logo-link">
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a7/Tokopedia.svg/960px-Tokopedia.svg.png?utm_source=commons.wikimedia.org&utm_campaign=imageinfo&utm_content=thumbnail" alt="Tokopedia Logo" className="shop-logo-img" />
              </a>
              <a href="https://www.lazada.co.id/" target="_blank" rel="noopener noreferrer" className="shop-logo-link">
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/4/4d/Lazada_%282019%29.svg/960px-Lazada_%282019%29.svg.png?utm_source=commons.wikimedia.org&utm_campaign=imageinfo&utm_content=thumbnail" alt="Lazada Logo" className="shop-logo-img" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* OUR WORK POINTS SECTION (With Motion Reveal) */}
      <section className="services-work-section">
        <div className="section-container">
          <div className="section-header-center reveal">
            <span className="section-badge">{t.workBadge}</span>
            <h2 className="section-title">{t.workTitle}</h2>
            <p className="section-desc-center">
              {t.workDesc}
            </p>
          </div>

          <div className="services-grid">
            <div className="service-card reveal-scale reveal-delay-1">
              <div className="service-icon-wrapper"><Truck size={24} /></div>
              <h3>{t.work1Title}</h3>
              <p>{t.work1Desc}</p>
            </div>
            <div className="service-card reveal-scale reveal-delay-2">
              <div className="service-icon-wrapper"><Check size={24} /></div>
              <h3>{t.work2Title}</h3>
              <p>{t.work2Desc}</p>
            </div>
            <div className="service-card reveal-scale reveal-delay-3">
              <div className="service-icon-wrapper"><Headphones size={24} /></div>
              <h3>{t.work3Title}</h3>
              <p>{t.work3Desc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL SECTION (With Motion Reveal) */}
      <section className="services-testimonial-section reveal-scale">
        <div className="section-container">
          <div className="testimonial-card">
            <div className="stars-row">
              <Star size={18} fill="currentColor" />
              <Star size={18} fill="currentColor" />
              <Star size={18} fill="currentColor" />
              <Star size={18} fill="currentColor" />
              <Star size={18} fill="currentColor" />
            </div>
            <blockquote className="testimonial-quote">
              "{t.testimonialQuote}"
            </blockquote>
            <cite className="testimonial-author">{t.testimonialAuthor}</cite>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-main-grid">
            <div className="footer-col footer-col-brand">
              <p className="footer-brand-desc">{t.footerDesc}</p>
            </div>

            <div className="footer-col footer-col-links">
              <h4 className="footer-col-title">{lang === "ar" ? "روابط سريعة" : lang === "id" ? "Navigasi Cepat" : "Quick Links"}</h4>
              <ul className="footer-links-list">
                <li><button onClick={() => navigate("/")}>{t.navHome}</button></li>
                <li><button onClick={() => navigate("/about")}>{t.navAbout}</button></li>
                <li><button onClick={() => navigate("/services")}>{t.navServices}</button></li>
                <li><button onClick={() => navigate("/check-rates")}>{t.navCheckRates}</button></li>
                <li><button onClick={() => navigate("/contact")}>{t.navContact}</button></li>
              </ul>
            </div>

            <div className="footer-col footer-col-services">
              <h4 className="footer-col-title">{lang === "ar" ? "وجهات الخدمة" : lang === "id" ? "Cakupan Destinasi" : "Service Coverage"}</h4>
              <ul className="footer-links-list text-muted-list">
                <li>🇾🇪 Yemen (Tarim, Mukalla, Aden, Sana'a)</li>
                <li>🇸🇦 Saudi Arabia (Jeddah, Riyadh, Makkah)</li>
                <li>📦 Air & Sea Cargo Shipping</li>
                <li>💵 Outbound & Inbound Money Transfer</li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p className="copyright-text">
              &copy; {new Date().getFullYear()} INDAK. {t.footerCopy}
            </p>
            {/* <div className="dev-tag">
              <span className="dev-by">{lang === "ar" ? "تطوير بواسطة" : "Dev By"}</span>
              <span className="dev-name">~ YallaBob ~</span>
            </div> */}
          </div>
        </div>
      </footer>
    </div>
  );
}
